import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";
import { Input } from "../components/ui/input";
import SmartSearchButton from "../components/SmartSearchButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Filter, X, Search, List as ListIcon, MapPin, BellPlus, Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import MapView from "../components/MapView";
import SponsoredAdSlot from "../components/SponsoredAdSlot";
import { useTranslation } from "react-i18next";
import { FUEL_OPTIONS, DRIVE_TYPES, EMISSION_CLASSES, COLOR_BASES, CONDITION_OPTIONS, MOTO_BRANDS, BIKE_BRANDS } from "../lib/listingConstants";
import { useVehicleCategories } from "../lib/systemConfig";
import BrandLogo from "../components/brand/BrandLogo";

const _num = (v) => Number(v).toLocaleString("de-DE");
const SALE_TYPE_LABELS = { fixed: "Festpreis", auction: "Auktion", rental: "Miete", pawn: "Pfand / Refinanzieren" };
// Entfernbare „aktive Filter"-Chips (ohne die Toggle-Quick-Filter unten, um
// Doppelung zu vermeiden). Reihenfolge = Anzeigereihenfolge.
const ACTIVE_FILTER_CHIPS = [
    ["q", (v) => `Suche: ${v}`],
    ["brand", (v) => `Marke: ${v}`],
    ["vehicle_type", (v) => `Kategorie: ${v}`],
    ["model", (v) => `Modell: ${v}`],
    ["sale_type", (v) => `Angebot: ${SALE_TYPE_LABELS[v] || v}`],
    ["min_price", (v) => `ab ${_num(v)} €`],
    ["max_price", (v) => `bis ${_num(v)} €`],
    ["min_year", (v) => `ab Bj. ${v}`],
    ["min_mileage", (v) => `ab ${_num(v)} km`],
    ["max_mileage", (v) => `bis ${_num(v)} km`],
    ["condition", (v) => `Zustand: ${v}`],
    ["fuel", (v) => `Antrieb: ${v}`],
    ["drive_type", (v) => `Antriebsart: ${v}`],
    ["emission_class", (v) => `Schadstoff: ${v}`],
    ["color_base", (v) => `Farbe: ${v}`],
];

export default function SearchPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const category = searchParams.get("category") || "motorcycle";
    const { fahrrad: sysBike } = useVehicleCategories();
    const { t } = useTranslation();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [favIds, setFavIds] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [view, setView] = useState(searchParams.get("view") || "list");  // 'list' | 'map'
    const [mapItems, setMapItems] = useState([]);
    const [mapLoading, setMapLoading] = useState(false);
    const { user } = useAuth();

    const [filters, setFilters] = useState({
        brand: searchParams.get("brand") || "",
        vehicle_type: searchParams.get("vehicle_type") || "",
        model: searchParams.get("model") || "",
        sale_type: searchParams.get("sale_type") || "",
        min_price: searchParams.get("min_price") || "",
        max_price: searchParams.get("max_price") || "",
        min_mileage: searchParams.get("min_mileage") || "",
        max_mileage: searchParams.get("max_mileage") || "",
        min_year: searchParams.get("min_year") || "",
        condition: searchParams.get("condition") || "",
        fuel: searchParams.get("fuel") || "",
        drive_type: searchParams.get("drive_type") || "",
        emission_class: searchParams.get("emission_class") || "",
        color_base: searchParams.get("color_base") || "",
        accident_free: searchParams.get("accident_free") || "",
        sharing_enabled: searchParams.get("sharing_enabled") || "",
        reduced_only: searchParams.get("reduced_only") || "",
        // Feb-2026 (User-Wunsch 26 May v3): Filter-Chips Übergabe/Lieferung
        pickup_available: searchParams.get("pickup_available") || "",
        pickup_with_registration: searchParams.get("pickup_with_registration") || "",
        pickup_unregistered_transport: searchParams.get("pickup_unregistered_transport") || "",
        delivery_nationwide: searchParams.get("delivery_nationwide") || "",
        q: searchParams.get("q") || "",
        sort: searchParams.get("sort") || "newest",
    });

    // Slider state mirrors filters but stores [min,max] tuples so the
    // shadcn Slider can render without converting strings on every render.
    const PRICE_MAX = 50000;
    const MILEAGE_MAX = 100000;
    const [priceRange, setPriceRange] = useState([
        Number(searchParams.get("min_price")) || 0,
        Number(searchParams.get("max_price")) || PRICE_MAX,
    ]);
    const [mileageRange, setMileageRange] = useState([
        Number(searchParams.get("min_mileage")) || 0,
        Number(searchParams.get("max_mileage")) || MILEAGE_MAX,
    ]);

    // Phase 3.65 — Saved-Search-Hinweis: eingeloggte User ohne aktive Suchen
    const [hasSavedSearches, setHasSavedSearches] = useState(true);
    const [savedSearchBannerDismissed, setSavedSearchBannerDismissed] = useState(
        () => typeof window !== "undefined" && localStorage.getItem("tw_match_promo_dismissed") === "1"
    );
    useEffect(() => {
        if (!user) {
            setHasSavedSearches(true);
            return;
        }
        api.get("/saved-searches")
            .then((r) => setHasSavedSearches((r.data || []).some((s) => !s.paused)))
            .catch(() => setHasSavedSearches(true));
    }, [user]);
    const dismissPromo = () => {
        setSavedSearchBannerDismissed(true);
        try { localStorage.setItem("tw_match_promo_dismissed", "1"); } catch (e) { /* ignore */ }
    };

    // Phase 3.70 — Banner-Werbeplätze (admin-konfigurierbar)
    const [adsLayout, setAdsLayout] = useState({
        listing_interval: 4,
        show_above_list: true,
        show_below_list: true,
        show_between_list: true,
        show_side_right: true,
    });
    useEffect(() => {
        api.get("/ads/layout-config")
            .then((r) => setAdsLayout((p) => ({ ...p, ...r.data })))
            .catch(() => {});
    }, []);

    const fetchResults = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams({ category });
        Object.entries(filters).forEach(([k, v]) => {
            if (v && v !== "any" && v !== "all") params.set(k, v);
        });
        try {
            const { data } = await api.get(`/listings?${params.toString()}`);
            setResults(data);
        } finally {
            setLoading(false);
        }
    }, [category, filters]);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    // Fetch geo data when switching to map view
    useEffect(() => {
        if (view !== "map") return;
        let cancelled = false;
        (async () => {
            setMapLoading(true);
            const params = new URLSearchParams({ category });
            Object.entries(filters).forEach(([k, v]) => {
                if (v && v !== "any" && v !== "all" && k !== "sort") params.set(k, v);
            });
            try {
                const { data } = await api.get(`/listings/geo?${params.toString()}`);
                if (!cancelled) setMapItems(data.items || []);
            } finally {
                if (!cancelled) setMapLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [view, category, filters]);

    useEffect(() => {
        if (user && user !== false) {
            api.get("/favorites/ids").then((r) => setFavIds(r.data)).catch((e) => console.error("Failed to load favorite ids", e));
        }
    }, [user]);

    const updateFilter = (k, v) => {
        setFilters((prev) => ({ ...prev, [k]: v }));
        const newParams = new URLSearchParams(searchParams);
        if (v && v !== "any" && v !== "all") newParams.set(k, v);
        else newParams.delete(k);
        setSearchParams(newParams);
    };

    // Aktive Filter als entfernbare Chips (User-Wunsch). Entfernen setzt den
    // jeweiligen Filter zurück + synchronisiert ggf. die Slider.
    const removeFilter = (key) => {
        updateFilter(key, "");
        if (key === "min_price") setPriceRange((p) => [0, p[1]]);
        else if (key === "max_price") setPriceRange((p) => [p[0], PRICE_MAX]);
        else if (key === "min_mileage") setMileageRange((p) => [0, p[1]]);
        else if (key === "max_mileage") setMileageRange((p) => [p[0], MILEAGE_MAX]);
    };

    const toggleFav = async (id) => {
        if (!user || user === false) {
            window.location.href = "/login";
            return;
        }
        const { data } = await api.post(`/favorites/${id}`);
        setFavIds((prev) => (data.favorited ? [...prev, id] : prev.filter((x) => x !== id)));
    };

    const switchCategory = (cat) => {
        const np = new URLSearchParams();
        np.set("category", cat);
        setSearchParams(np);
        setFilters({ brand: "", vehicle_type: "", model: "", sale_type: "", min_price: "", max_price: "", min_mileage: "", max_mileage: "", min_year: "", condition: "", fuel: "", drive_type: "", emission_class: "", color_base: "", accident_free: "", sharing_enabled: "", reduced_only: "", q: "", sort: "newest" });
        setPriceRange([0, PRICE_MAX]);
        setMileageRange([0, MILEAGE_MAX]);
    };

    const commitPriceRange = (vals) => {
        setPriceRange(vals);
        const [lo, hi] = vals;
        setFilters((p) => ({
            ...p,
            min_price: lo > 0 ? String(lo) : "",
            max_price: hi < PRICE_MAX ? String(hi) : "",
        }));
    };
    const commitMileageRange = (vals) => {
        setMileageRange(vals);
        const [lo, hi] = vals;
        setFilters((p) => ({
            ...p,
            min_mileage: lo > 0 ? String(lo) : "",
            max_mileage: hi < MILEAGE_MAX ? String(hi) : "",
        }));
    };

    const saveCurrentSearch = async () => {        if (!user) {
            toast.error("Bitte zuerst anmelden, um Suchen zu speichern");
            return;
        }
        const defaultName = [
            isMoto ? "Motorrad" : "Fahrrad",
            filters.brand || null,
            filters.max_price ? `bis ${Number(filters.max_price).toLocaleString("de-DE")} €` : null,
            filters.q || null,
        ].filter(Boolean).join(" · ");
        const name = window.prompt(t("search.save_search_prompt"), defaultName || "");
        if (!name) return;
        try {
            const payload = {
                name,
                category,
                frequency: "daily",
            };
            ["brand", "condition", "q", "fuel", "drive_type", "emission_class", "color_base"].forEach((k) => {
                if (filters[k]) payload[k] = filters[k];
            });
            ["min_price", "max_price", "min_year", "min_mileage", "max_mileage"].forEach((k) => {
                if (filters[k]) payload[k] = Number(filters[k]);
            });
            await api.post("/saved-searches", payload);
            toast.success(t("search.saved_toast", { name }));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    const isMoto = category === "motorcycle";
    // Feb-2026 (User-Wunsch 26 May): Komplette Marken-Liste aus listingConstants
    // (statt der bisherigen 10-Marken-Hardcoded-Liste). Identisch zur Auswahl
    // beim Fahrzeug-Inserieren.
    const brands = isMoto ? MOTO_BRANDS : BIKE_BRANDS;

    // Phase 4.10 (H2) — Natural-Language-Search via Gemini
    const [nlqInput, setNlqInput] = useState("");
    const [nlqLoading, setNlqLoading] = useState(false);
    const [nlqApplied, setNlqApplied] = useState(null);
    const runNlq = async () => {
        const query = nlqInput.trim();
        if (query.length < 3) {
            toast.info("Bitte mindestens 3 Zeichen eingeben.");
            return;
        }
        setNlqLoading(true);
        try {
            const { data } = await api.post("/listings/ai-search-nlq", { query });
            const f = data.filters || {};
            // Switch category first (resets filters); then apply structured filters.
            const np = new URLSearchParams();
            const newCat = f.category === "bicycle" ? "bicycle" : (f.category === "motorcycle" ? "motorcycle" : category);
            np.set("category", newCat);
            const cleanFilters = { brand: "", vehicle_type: "", model: "", sale_type: "", min_price: "", max_price: "", min_mileage: "", max_mileage: "", min_year: "", condition: "", fuel: "", drive_type: "", emission_class: "", color_base: "", accident_free: "", sharing_enabled: "", reduced_only: "", q: "", sort: "newest" };
            Object.entries(f).forEach(([k, v]) => {
                if (k === "category") return;
                if (v === null || v === undefined || v === "") return;
                np.set(k, String(v));
                cleanFilters[k] = String(v);
            });
            if (data.q_residual) {
                np.set("q", data.q_residual);
                cleanFilters.q = data.q_residual;
            }
            setSearchParams(np);
            setFilters(cleanFilters);
            setPriceRange([Number(f.min_price) || 0, Number(f.max_price) || PRICE_MAX]);
            setMileageRange([Number(f.min_mileage) || 0, Number(f.max_mileage) || MILEAGE_MAX]);
            const applied = Object.keys(f).length + (data.q_residual ? 1 : 0);
            setNlqApplied({ count: applied, mocked: data.mocked });
            if (data.mocked) {
                toast.warning("KI nicht erreichbar — nur Volltext-Suche aktiv.", { duration: 4000 });
            } else {
                toast.success(`${applied} Filter aus deiner Eingabe übernommen.`);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "KI-Suche fehlgeschlagen.");
        } finally {
            setNlqLoading(false);
        }
    };

    const FilterPanel = (
        <aside className="w-full lg:w-80 flex-shrink-0 space-y-6" data-testid="filters-panel">
            <div className="bg-white border border-gray-200 rounded-sm">
                <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-display font-bold text-lg">Filter</h3>
                    <button
                        className="lg:hidden text-gray-500"
                        onClick={() => setShowFilters(false)}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5 space-y-5">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t("search.filter_search_q")}</label>
                        <div className="flex gap-2">
                            <Input
                                value={filters.q}
                                onChange={(e) => updateFilter("q", e.target.value)}
                                placeholder={t("search.filter_q_placeholder")}
                                className="rounded-sm flex-1 border-2 border-emerald-500 focus-visible:ring-emerald-400 placeholder:text-emerald-700/70"
                                data-testid="filter-q"
                            />
                            <SmartSearchButton query={filters.q} onApplyFilters={(parsed) => {
                                const mapped = {};
                                if (parsed.brand) mapped.brand = parsed.brand;
                                if (parsed.year_min) mapped.year_min = String(parsed.year_min);
                                if (parsed.year_max) mapped.year_max = String(parsed.year_max);
                                if (parsed.power_kw_min) mapped.power_kw_min = String(parsed.power_kw_min);
                                if (parsed.power_kw_max) mapped.power_kw_max = String(parsed.power_kw_max);
                                if (parsed.category) mapped.category = parsed.category;
                                if (parsed.fuel) mapped.fuel = parsed.fuel;
                                if (parsed.condition) mapped.condition = parsed.condition;
                                if (parsed.price_max) {
                                    setPriceRange((p) => [p[0], Math.min(PRICE_MAX, parsed.price_max)]);
                                }
                                setFilters((prev) => ({ ...prev, ...mapped }));
                            }} />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t("search.filter_brand")}</label>
                        <Select value={filters.brand || "all"} onValueChange={(v) => updateFilter("brand", v === "all" ? "" : v)}>
                            <SelectTrigger className="rounded-sm" data-testid="filter-brand" aria-label="Marke filtern"><SelectValue placeholder={t("search.filter_brand_any")} /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">{t("search.filter_brand_any")}</SelectItem>
                                {brands.map((b) => (
                                    <SelectItem key={b} value={b}>
                                        <span className="inline-flex items-center gap-2"><BrandLogo brand={b} size={20} /> {b}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div data-testid="filter-price-slider-wrap">
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t("search.filter_price")}</label>
                        <div className="flex items-center justify-between text-xs font-mono-num text-gray-700 mb-2">
                            <span data-testid="filter-price-lo">{priceRange[0].toLocaleString("de-DE")} €</span>
                            <span data-testid="filter-price-hi">{priceRange[1] >= PRICE_MAX ? "50.000+ €" : `${priceRange[1].toLocaleString("de-DE")} €`}</span>
                        </div>
                        <Slider
                            value={priceRange}
                            min={0}
                            max={PRICE_MAX}
                            step={500}
                            onValueChange={setPriceRange}
                            onValueCommit={commitPriceRange}
                            className="mb-1"
                            data-testid="filter-price-slider"
                        />
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            <Input
                                type="number"
                                placeholder={t("search.from")}
                                value={filters.min_price}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    updateFilter("min_price", v);
                                    setPriceRange([Number(v) || 0, priceRange[1]]);
                                }}
                                className="rounded-sm font-mono-num text-sm h-9"
                                data-testid="filter-min-price"
                            />
                            <Input
                                type="number"
                                placeholder={t("search.to")}
                                value={filters.max_price}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    updateFilter("max_price", v);
                                    setPriceRange([priceRange[0], Number(v) || PRICE_MAX]);
                                }}
                                className="rounded-sm font-mono-num text-sm h-9"
                                data-testid="filter-max-price"
                            />
                        </div>
                    </div>

                    {isMoto && (
                        <div data-testid="filter-mileage-slider-wrap">
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t("search.filter_mileage")}</label>
                            <div className="flex items-center justify-between text-xs font-mono-num text-gray-700 mb-2">
                                <span data-testid="filter-mileage-lo">{mileageRange[0].toLocaleString("de-DE")} km</span>
                                <span data-testid="filter-mileage-hi">{mileageRange[1] >= MILEAGE_MAX ? "100.000+ km" : `${mileageRange[1].toLocaleString("de-DE")} km`}</span>
                            </div>
                            <Slider
                                value={mileageRange}
                                min={0}
                                max={MILEAGE_MAX}
                                step={1000}
                                onValueChange={setMileageRange}
                                onValueCommit={commitMileageRange}
                                data-testid="filter-mileage-slider"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t("search.filter_year_from")}</label>
                        <Select value={filters.min_year || "any"} onValueChange={(v) => updateFilter("min_year", v === "any" ? "" : v)}>
                            <SelectTrigger className="rounded-sm" data-testid="filter-min-year" aria-label="Erstzulassung ab filtern"><SelectValue placeholder={t("search.filter_condition_any")} /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">{t("search.filter_condition_any")}</SelectItem>
                                {[2024, 2022, 2020, 2018, 2015, 2010].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{t("search.filter_condition")}</label>
                        <Select value={filters.condition || "any"} onValueChange={(v) => updateFilter("condition", v === "any" ? "" : v)}>
                            <SelectTrigger className="rounded-sm" data-testid="filter-condition" aria-label="Zustand filtern"><SelectValue placeholder={t("search.filter_condition_any")} /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">{t("search.filter_condition_any")}</SelectItem>
                                {CONDITION_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Angebotstyp</label>
                        <Select value={filters.sale_type || "any"} onValueChange={(v) => updateFilter("sale_type", v === "any" ? "" : v)}>
                            <SelectTrigger className="rounded-sm" data-testid="filter-sale-type" aria-label="Angebotstyp filtern"><SelectValue placeholder="Alle" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">{t("search.any", "Alle")}</SelectItem>
                                <SelectItem value="fixed">Festpreis</SelectItem>
                                <SelectItem value="auction">Auktion</SelectItem>
                                <SelectItem value="rental">Miete / Vermietung</SelectItem>
                                <SelectItem value="pawn">Pfand / Refinanzieren</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {isMoto && (
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Kraftstoff</label>
                            <Select value={filters.fuel || "any"} onValueChange={(v) => updateFilter("fuel", v === "any" ? "" : v)}>
                                <SelectTrigger className="rounded-sm" data-testid="filter-fuel" aria-label="Kraftstoff filtern"><SelectValue placeholder="Alle" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">{t("search.any", "Alle")}</SelectItem>
                                    {FUEL_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Antrieb</label>
                        <Select value={filters.drive_type || "any"} onValueChange={(v) => updateFilter("drive_type", v === "any" ? "" : v)}>
                            <SelectTrigger className="rounded-sm" data-testid="filter-drive-type" aria-label="Antriebsart filtern"><SelectValue placeholder="Alle" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">{t("search.any", "Alle")}</SelectItem>
                                {DRIVE_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {isMoto && (
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Schadstoffklasse</label>
                            <Select value={filters.emission_class || "any"} onValueChange={(v) => updateFilter("emission_class", v === "any" ? "" : v)}>
                                <SelectTrigger className="rounded-sm" data-testid="filter-emission-class" aria-label="Schadstoffklasse filtern"><SelectValue placeholder="Alle" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">{t("search.any", "Alle")}</SelectItem>
                                    {EMISSION_CLASSES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Farbbasis</label>
                        <Select value={filters.color_base || "any"} onValueChange={(v) => updateFilter("color_base", v === "any" ? "" : v)}>
                            <SelectTrigger className="rounded-sm" data-testid="filter-color-base" aria-label="Farbe filtern"><SelectValue placeholder="Alle" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">{t("search.any", "Alle")}</SelectItem>
                                {COLOR_BASES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-3 pt-1">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer" data-testid="filter-accident-free-wrap">
                            <Checkbox
                                checked={filters.accident_free === "true"}
                                onCheckedChange={(v) => updateFilter("accident_free", v ? "true" : "")}
                                data-testid="filter-accident-free" aria-label="Nur unfallfreie Fahrzeuge"
                            />
                            <span>Nur unfallfreie Fahrzeuge</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer" data-testid="filter-reduced-wrap">
                            <Checkbox
                                checked={filters.reduced_only === "true"}
                                onCheckedChange={(v) => updateFilter("reduced_only", v ? "true" : "")}
                                data-testid="filter-reduced-only" aria-label="Nur reduzierte Angebote"
                            />
                            <span>{t("search.reduced_only", "Nur reduzierte Angebote")}</span>
                        </label>
                    </div>

                    <Button
                        variant="outline"
                        onClick={() => {
                            setFilters({ brand: "", vehicle_type: "", model: "", sale_type: "", min_price: "", max_price: "", min_mileage: "", max_mileage: "", min_year: "", condition: "", fuel: "", drive_type: "", emission_class: "", color_base: "", accident_free: "", sharing_enabled: "", reduced_only: "", q: "", sort: "newest" });
                            setPriceRange([0, PRICE_MAX]);
                            setMileageRange([0, MILEAGE_MAX]);
                            setSearchParams({ category });
                        }}
                        className="w-full rounded-sm border-[#111827]"
                        data-testid="reset-filters-btn"
                    >
                        {t("search.filter_reset")}
                    </Button>
                </div>
            </div>
        </aside>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="search-page">
            {/* Category switcher */}
            <div className="mb-6">
                <div className="flex flex-wrap gap-2 mb-4">
                    <button
                        onClick={() => switchCategory("motorcycle")}
                        className={`px-5 py-2.5 rounded-sm font-bold text-sm uppercase tracking-wider transition-colors ${isMoto ? "bg-[#FFCC00] text-[#111827]" : "bg-white border border-gray-300 hover:border-[#111827]"}`}
                        data-testid="cat-motorcycle"
                    >
                        {t("nav.motorcycles")}
                    </button>
                    {sysBike && (
                    <button
                        onClick={() => switchCategory("bicycle")}
                        className={`px-5 py-2.5 rounded-sm font-bold text-sm uppercase tracking-wider transition-colors ${!isMoto ? "bg-[#FFCC00] text-[#111827]" : "bg-white border border-gray-300 hover:border-[#111827]"}`}
                        data-testid="cat-bicycle"
                    >
                        {t("nav.bicycles")}
                    </button>
                    )}
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight" data-testid="search-title">
                    {isMoto ? t("search.title_motorcycles") : t("search.title_bicycles")}
                </h1>
                <p className="text-gray-600 mt-1 text-sm">{t("search.results_count", { count: results.length })}</p>

                {/* Aktive Filter als entfernbare Chips (User-Wunsch) */}
                {(() => {
                    const chips = ACTIVE_FILTER_CHIPS.filter(([k]) => {
                        const v = filters[k];
                        return v && v !== "any" && v !== "all";
                    });
                    if (chips.length === 0) return null;
                    return (
                        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="active-filter-chips">
                            {chips.map(([k, fmt]) => (
                                <button
                                    key={k}
                                    onClick={() => removeFilter(k)}
                                    data-testid={`active-chip-${k}`}
                                    title="Filter entfernen"
                                    className="inline-flex items-center gap-1.5 bg-[#111827] text-white text-xs font-bold pl-3 pr-2 py-1.5 rounded-full hover:bg-black transition-colors"
                                >
                                    {fmt(filters[k])}
                                    <X className="w-3.5 h-3.5 opacity-70" />
                                </button>
                            ))}
                            <button
                                onClick={() => switchCategory(category)}
                                data-testid="clear-all-filters"
                                className="text-xs font-bold text-gray-500 hover:text-[#DC2626] underline underline-offset-2 ml-1"
                            >
                                Alle zurücksetzen
                            </button>
                        </div>
                    );
                })()}

                {/* Feb-2026 (User-Wunsch 26 May v3) — Quick-Filter-Chips für
                    Übergabe/Lieferung. Klick toggelt true/leer; bei aktivem
                    Filter wird ein gelber Solid-Style verwendet.
                    Feb-2026 (User-Wunsch 27 May) — Mini-Stats: zeigen wieviele
                    der aktuellen Treffer den Chip-Filter ebenfalls erfüllen. */}
                <div className="mt-3 flex flex-wrap gap-2" data-testid="quick-filter-chips">
                    {[
                        { k: "pickup_available", label: t("search.chip_pickup", "Sofortmitnahme / Abholung") },
                        { k: "pickup_with_registration", label: t("search.chip_registered", "Noch zugelassen (eVB)") },
                        { k: "pickup_unregistered_transport", label: t("search.chip_transport", "Auslieferungsfertig (Transport)") },
                        { k: "delivery_nationwide", label: t("search.chip_delivery", "Bundesweite Lieferung") },
                        { k: "accident_free", label: t("search.chip_accident_free", "Unfallfrei") },
                    ].map((chip) => {
                        const active = filters[chip.k] === "true";
                        const matchingCount = results.filter((r) => !!r[chip.k]).length;
                        const totalCount = results.length;
                        return (
                            <button
                                key={chip.k}
                                type="button"
                                onClick={() => setFilters((f) => ({ ...f, [chip.k]: active ? "" : "true" }))}
                                className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors inline-flex items-center gap-1.5 ${active ? "bg-[#FFCC00] border-[#111827] text-[#111827]" : "bg-white border-gray-300 text-gray-700 hover:border-[#111827]"}`}
                                data-testid={`chip-${chip.k}`}
                            >
                                <span>{chip.label}</span>
                                {totalCount > 0 && (
                                    <span
                                        className={`font-mono-num text-[10px] px-1.5 py-0.5 rounded-sm ${active ? "bg-[#111827] text-[#FFCC00]" : "bg-gray-100 text-gray-600 group-hover:bg-[#FFCC00]"}`}
                                        data-testid={`chip-${chip.k}-count`}
                                    >
                                        {matchingCount}/{totalCount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Phase 4.10 (H2) — Natural-Language-Search Banner */}
                <div className="mt-5 bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-sm p-4" data-testid="nlq-search-banner">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-amber-700" />
                        <span className="font-bold text-sm text-[#111827]">KI-Suche</span>
                        <span className="text-[10px] text-gray-600">— beschreibe in eigenen Worten, was du suchst</span>
                    </div>
                    <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                        <Input
                            value={nlqInput}
                            onChange={(e) => setNlqInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runNlq(); } }}
                            placeholder="z. B. BMW Tourer bis 18.000 € nicht älter als 2020 mit Topcase"
                            className="flex-1 min-w-0 rounded-sm bg-white"
                            data-testid="nlq-search-input"
                            maxLength={200}
                        />
                        <Button
                            onClick={runNlq}
                            disabled={nlqLoading || nlqInput.trim().length < 3}
                            className="bg-[#111827] hover:bg-black text-white rounded-sm h-10 px-4 font-bold uppercase tracking-wider text-xs whitespace-nowrap"
                            data-testid="nlq-search-submit"
                        >
                            {nlqLoading
                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Suche…</>
                                : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> KI-Suche</>}
                        </Button>
                    </div>
                    {nlqApplied && (
                        <div className="text-[11px] text-gray-700 mt-2 flex items-center gap-2 flex-wrap" data-testid="nlq-search-applied">
                            <span>✓ {nlqApplied.count} Filter aus deiner Eingabe übernommen{nlqApplied.mocked ? " (Fallback)" : ""}.</span>
                            {user && !nlqApplied.mocked && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        const name = window.prompt(
                                            "Wie soll diese KI-Suche heißen?",
                                            nlqInput.slice(0, 60),
                                        );
                                        if (!name) return;
                                        try {
                                            const payload = { name, category, frequency: "daily" };
                                            Object.entries(filters).forEach(([k, v]) => {
                                                if (!v || v === "any" || v === "all") return;
                                                if (["min_price","max_price","min_year","min_mileage","max_mileage"].includes(k)) {
                                                    payload[k] = Number(v);
                                                } else if (["brand","condition","q"].includes(k)) {
                                                    payload[k] = v;
                                                }
                                            });
                                            await api.post("/saved-searches", payload);
                                            toast.success(`„${name}" gespeichert — du wirst bei neuen Treffern benachrichtigt.`);
                                        } catch (err) {
                                            toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
                                        }
                                    }}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#111827] hover:bg-black text-white"
                                    data-testid="nlq-search-save"
                                    title="Diese Suche speichern und bei neuen passenden Inseraten benachrichtigt werden."
                                >
                                    <BellPlus className="w-3 h-3" /> Als Suche speichern + Alarm
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                <div className="hidden lg:block">{FilterPanel}</div>
                {showFilters && (
                    <div className="fixed inset-0 z-50 bg-black/80 lg:hidden">
                        <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                            {FilterPanel}
                        </div>
                    </div>
                )}

                <div className="flex-1 min-w-0">
                    <div className="flex items-center mb-4 gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            onClick={() => setShowFilters(true)}
                            className="lg:hidden border-[#111827] rounded-sm h-9"
                            data-testid="show-filters-btn"
                        >
                            <Filter className="w-4 h-4 mr-2" /> {t("search.filters")}
                        </Button>
                        {user && (
                            <Button
                                onClick={saveCurrentSearch}
                                variant="outline"
                                className="border-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs h-9"
                                data-testid="save-search-btn"
                            >
                                <BellPlus className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">{t("search.save_search")}</span><span className="sm:hidden">Speichern</span>
                            </Button>
                        )}
                        <div className="ml-auto flex items-center gap-2">
                            <div className="flex border border-gray-300 rounded-sm overflow-hidden" data-testid="view-toggle">
                                <button
                                    onClick={() => setView("list")}
                                    className={`px-2 sm:px-3 h-9 text-xs font-bold uppercase tracking-wider flex items-center gap-1 ${view === "list" ? "bg-[#111827] text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                                    data-testid="view-list-btn"
                                    aria-label={t("search.view_list")}
                                >
                                    <ListIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t("search.view_list")}</span>
                                </button>
                                <button
                                    onClick={() => setView("map")}
                                    className={`px-2 sm:px-3 h-9 text-xs font-bold uppercase tracking-wider flex items-center gap-1 border-l border-gray-300 ${view === "map" ? "bg-[#111827] text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                                    data-testid="view-map-btn"
                                    aria-label={t("search.view_map")}
                                >
                                    <MapPin className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t("search.view_map")}</span>
                                </button>
                            </div>
                            <Select value={filters.sort} onValueChange={(v) => updateFilter("sort", v)}>
                                <SelectTrigger className="w-[140px] sm:w-[200px] rounded-sm" data-testid="sort-select" aria-label="Sortierung wählen"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="newest">{t("search.sort_newest")}</SelectItem>
                                    <SelectItem value="best_match" data-testid="sort-option-best-match">Bestes Match zuerst</SelectItem>
                                    <SelectItem value="price_asc">{t("search.sort_price_asc")}</SelectItem>
                                    <SelectItem value="price_desc">{t("search.sort_price_desc")}</SelectItem>
                                    <SelectItem value="year_desc">{t("search.sort_year_desc")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Ranking-Transparenz (§5b UWG / P2B-VO) — Hauptkriterien der Reihung offenlegen */}
                    <details className="mb-4 bg-white border border-gray-200 rounded-sm" data-testid="ranking-transparency">
                        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-bold text-[#111827] hover:text-[#C2410C]">
                            {t("search.sort_transparency", "Wie werden die Ergebnisse sortiert? (Transparenz)")}
                        </summary>
                        <div className="px-3 pb-3 text-xs text-gray-600 space-y-1.5">
                            <p>{t("search.sort_p1", "Standardmäßig zeigen wir die neuesten Inserate zuerst. Du kannst jederzeit selbst nach Preis, Baujahr oder „Bestes Match“ umsortieren.")}</p>
                            <p>{t("search.sort_p2", "„Bestes Match“ gewichtet die Übereinstimmung mit deinen Filtern und gespeicherten Suchen (Match-Score). Die organische Reihung berücksichtigt sonst keine Zahlungen.")}</p>
                            <p>{t("search.sort_p3", "Hervorgehobene/geboostete Inserate können innerhalb der Liste weiter oben erscheinen und sind als solche gekennzeichnet. Bezahlte Anzeigen werden separat und deutlich als „Anzeige“ ausgewiesen.")}</p>
                        </div>
                    </details>

                    {view === "map" ? (
                        mapLoading ? (
                            <div className="bg-white rounded-sm border border-gray-200 h-[600px] animate-pulse" />
                        ) : (
                            <MapView items={mapItems} />
                        )
                    ) : loading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white rounded-sm border border-gray-200 h-48 animate-pulse" />
                            ))}
                        </div>
                    ) : results.length === 0 ? (
                        <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="no-results">
                            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="font-display font-bold text-xl mb-2">Keine Inserate gefunden</h3>
                            <p className="text-gray-600 text-sm">Versuche es mit anderen Filtern.</p>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="results-list">
                            {/* Phase 3.65 — Saved-Search-Promo für eingeloggte User ohne aktive Suchen */}
                            {user && !hasSavedSearches && !savedSearchBannerDismissed && results.length > 0 && (
                                <div
                                    className="flex items-start gap-3 p-4 bg-[#FFF8DB] border border-[#FFCC00] rounded-sm"
                                    data-testid="match-score-promo-banner"
                                >
                                    <BellPlus className="w-5 h-5 text-[#111827] flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-display font-bold text-sm text-[#111827] mb-1">
                                            Speichern Sie diese Suche und sehen Sie sofort, welche Inserate zu Ihnen passen.
                                        </p>
                                        <p className="text-xs text-gray-700">
                                            Jedes Inserat bekommt einen Match-Score (0–100 %), sodass Sie die besten Treffer auf einen Blick erkennen. Sortieren Sie zusätzlich mit „Bestes Match zuerst".
                                        </p>
                                    </div>
                                    <button
                                        onClick={dismissPromo}
                                        className="text-gray-500 hover:text-gray-800 flex-shrink-0"
                                        title="Hinweis ausblenden"
                                        data-testid="match-score-promo-dismiss"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            {/* Phase 3.25/3.70 — Banner über der Liste (admin-konfigurierbar) */}
                            {adsLayout.show_above_list && (
                                <div data-testid="ads-slot-above-list">
                                    <SponsoredAdSlot limit={3} />
                                </div>
                            )}
                            {results.map((l, idx) => (
                                <React.Fragment key={l.id}>
                                    <ListingCard
                                        listing={l}
                                        onToggleFavorite={toggleFav}
                                        isFavorited={favIds.includes(l.id)}
                                    />
                                    {adsLayout.show_between_list && adsLayout.listing_interval > 0 &&
                                        (idx + 1) % adsLayout.listing_interval === 0 && idx !== results.length - 1 && (
                                        <div data-testid={`ads-slot-between-${idx + 1}`} className="my-1">
                                            <SponsoredAdSlot limit={1} />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                            {/* Phase 3.70 — Banner unter der Liste */}
                            {adsLayout.show_below_list && results.length > 0 && (
                                <div data-testid="ads-slot-below-list" className="mt-2">
                                    <SponsoredAdSlot limit={2} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {adsLayout.show_side_right && (
                    <aside className="hidden xl:block w-60 shrink-0" data-testid="ads-slot-side-right">
                        <div className="sticky top-24 space-y-3">
                            <SponsoredAdSlot variant="card" limit={3} />
                        </div>
                    </aside>
                )}
            </div>
        </div>
    );
}
